import React, { useState, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/system';
import { motion, useInView } from 'framer-motion';
import AAdsBanner from './AAdsBanner';

const AnimatedGlowBox = styled(motion(Box), {
  shouldForwardProp: (prop) => prop !== 'godMode',
})(({ theme, godMode }) => ({
  width: '380px',
  minHeight: '380px',
  padding: '30px',
  margin: '20px auto',
  // Without border-box the padding made the box 60px wider than its
  // centered wrapper, so it hung off to the right on mobile/tablet.
  boxSizing: 'border-box',
  // In God Mode the box goes transparent and GodCircleTwo renders on a
  // separate stationary layer behind it (see render), so dragging the box
  // aside leaves the image in place for the viewer to see.
  backgroundColor: godMode ? 'transparent' : '#1a237e',
  borderRadius: '10px',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
    width: '90%',
    // Lock the box to at least as tall as it is wide. On mobile / narrow
    // split-screen (sm = ≤600px) this gives a square minimum; if the inner
    // content (title, input, button, result) needs more vertical room the
    // box can grow taller, but it never collapses back to a wide rectangle.
    minHeight: '90vw',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#FFFFFF',
  fontSize: '2rem',
  marginBottom: '20px',
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  '@media (max-width: 600px)': {
    fontSize: '1rem',
  },
}));

const RetroInput = styled('input')(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  fontSize: '1.2rem',
  color: '#FFFFFF',
  backgroundColor: '#1a237e',
  border: '4px solid #FFFFFF',
  borderRadius: '4px',
  padding: '10px',
  width: '80%',
  maxWidth: '400px',
  textAlign: 'center',
  outline: 'none',
  marginBottom: '20px',
  '&:focus': {
    boxShadow: '0 0 20px #9932CC',
  },
  '@media (max-width: 600px)': {
    fontSize: '0.8rem',
    padding: '8px',
  },
}));

// Move this up with your other styled components at the top of the file
const SectionTitle = styled(Typography)(({ theme }) => ({
    fontFamily: "'Press Start 2P', cursive",
    color: '#FFFFFF',
    fontSize: '2.4rem',
    marginTop: '40px',
    marginBottom: '20px',
    textShadow: `
      -1px -1px 0 #000,
      1px -1px 0 #000,
      -1px 1px 0 #000,
      1px 1px 0 #000
    `,
    '@media (max-width: 600px)': {
      fontSize: '1.2rem',
      marginTop: '14px',
      marginBottom: '8px',
    },
  }));

const RetroButton = styled(Button)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  fontSize: '1rem',
  color: '#FFFFFF',
  backgroundColor: '#1a237e',
  border: '4px solid #FFFFFF',
  borderRadius: '4px',
  padding: '10px 20px',
  margin: '10px',
  cursor: 'pointer',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#1a237e',
    boxShadow: '0 0 20px #9932CC',
  },
  '@media (max-width: 600px)': {
    fontSize: '0.7rem',
    padding: '8px 16px',
  },
}));

const ResultText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#FFFFFF',
  fontSize: '1.4rem',
  marginTop: '20px',
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  '@media (max-width: 600px)': {
    fontSize: '0.8rem',
  },
}));

const YellowDividerBar = styled(motion('div'))(({ theme }) => ({
    width: '80%',
    height: '4.4px',
    backgroundColor: '#FFF59D', // Light yellow color
    margin: '30px auto 30px',
    transformOrigin: 'center',
    position: 'relative',
    zIndex: 1,
    boxShadow: `
      0 0 5px #FFF59D,
      0 0 10px #FFF59D,
      0 0 20px #FFF59D,
      0 0 30px #FFEB3B,
      0 0 50px #FFEB3B
    `,
    [theme.breakpoints.down('sm')]: {
      margin: '12px auto',
      width: '95%',
    },
  }));
  
  const YellowMotionDivider = () => {
    return (
      <YellowDividerBar
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{
          duration: 2,
          ease: "easeInOut",
        }}
      />
    );
  };

const UsdToBtcConverter = ({ tickerComponent, godMode = false }) => {
  const [usdAmount, setUsdAmount] = useState('');
  const [btcResult, setBtcResult] = useState(null);
  const [satoshiResult, setSatoshiResult] = useState(null);
  const boxRef = React.useRef(null);
  const convertAudioRef = useRef(null);
  const isInView = useInView(boxRef, { once: true, margin: '-100px' });

  const playConvertSound = () => {
    if (convertAudioRef.current) {
      convertAudioRef.current.currentTime = 0;
      convertAudioRef.current.play().catch(error => console.log('Error playing convert sound:', error));
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    if (value === '' || (Number(value) <= 1000000000000 && !isNaN(value))) {
      setUsdAmount(value);
    }
  };

  const convertToBTC = async () => {
    playConvertSound();
    try {
      const response = await fetch('/api/btc-price');
      const data = await response.json();
      const btcPrice = data.price;
      const btcValue = (Number(usdAmount) / btcPrice).toFixed(8);
      setBtcResult(btcValue);
      setSatoshiResult(null);
    } catch (error) {
      console.error('Error converting to BTC:', error);
    }
  };
  

  const convertToSatoshi = async () => {
    playConvertSound();
    try {
      const response = await fetch('/api/btc-price');
      const data = await response.json();
      const btcPrice = data.price;
      const btcValue = Number(usdAmount) / btcPrice;
      const satoshiValue = Math.round(btcValue * 100000000).toLocaleString();
      setSatoshiResult(satoshiValue);
      setBtcResult(null);
    } catch (error) {
      console.error('Error converting to Satoshi:', error);
    }
  };


  return (
    <>
      <Box
        sx={{
          position: 'relative',
          margin: '20px auto',
          width: { xs: '90%', sm: '380px' },
        }}
      >
        {/* Stationary god circle layer: stays put while the box is dragged
            aside, so the viewer can see the image unobstructed. */}
        {godMode && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'url(https://media.satoshiadvance.com/images/GodCircleTwo.png)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              backgroundSize: 'contain',
              borderRadius: '10px',
              pointerEvents: 'none',
            }}
          />
        )}
      <AnimatedGlowBox
        godMode={godMode}
        ref={boxRef}
        style={{ margin: 0, width: '100%' }}
        // Click-and-drag left/right (same feel as the spinner logo) so the box
        // can be slid out of the way of the god circle imagery.
        drag="x"
        dragMomentum={false}
        dragElastic={0.15}
        dragConstraints={{ left: -600, right: 600 }}
        whileDrag={{ cursor: 'grabbing' }}
        sx={{ cursor: 'grab' }}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1, boxShadow: '0 0 20px #9932CC, 0 0 30px #9932CC, 0 0 40px #9932CC' } : {}}
        whileHover={{
          boxShadow: '0 0 40px #BA55D3, 0 0 60px #BA55D3, 0 0 80px #BA55D3',
        }}
        transition={{
          duration: 1.5,
          ease: 'easeInOut',
        }}
      >
        <audio ref={convertAudioRef} src="/RetroConvert2.mp3" />
        <TitleText>USD to BTC/SATS Converter Tool</TitleText>
        <Box>
        <RetroInput
  type="number"           // Changed from "text" to "number"
  inputMode="decimal"     // Shows decimal keyboard on mobile
  pattern="[0-9]*"       // Ensures only numbers are allowed
  value={usdAmount}
  onChange={handleInputChange}
  placeholder="Enter USD amount"
/>
        </Box>
        <Box>
          <RetroButton onClick={convertToBTC}>
            Convert to BTC
          </RetroButton>
          <RetroButton onClick={convertToSatoshi}>
            Convert to Sats
          </RetroButton>
        </Box>
        {btcResult && (
          <ResultText>
            {btcResult} BTC
          </ResultText>
        )}
        {satoshiResult && (
          <ResultText>
            {satoshiResult} Sats
          </ResultText>
        )}
      </AnimatedGlowBox>
      </Box>
      {tickerComponent}
      {/* Ad banner directly above the Education & Resources title (the title
          lives in this component, after the converter, not in EducationLinks) */}
      <AAdsBanner />
      <SectionTitle>
      Education & Resources
      </SectionTitle>
      <YellowMotionDivider />
    </>
  );
};

export default UsdToBtcConverter;